import React, { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { GalleryItem as GalleryItemComponent } from './GalleryItem'
import { VideoDownloadModal } from './VideoDownloadModal'
import { VideoCreationConfirmModal } from './VideoCreationConfirmModal'
import { FullImageModal } from 'pages/ai-studio/tabs/FashionEditorTab/components/FullImageModal'

import { StyleShotItem, GalleryItem } from '../types'
import { aiStudioService } from '../../../../service/aiStudio'

interface StyleShotGridProps {
  items: StyleShotItem[]
  onDownload: (item: StyleShotItem) => void
  onSwitchToStyleClip?: () => void
  onRefetchVideos?: () => void
  onStartVideoPolling?: (requestId: string) => void
  onDelete?: (item: StyleShotItem) => void
  isLoading?: boolean
  // 페이지네이션 관련
  currentPage?: number
  totalPages?: number
  onNextPage?: () => void
  onPrevPage?: () => void
}

export const StyleShotGrid: React.FC<StyleShotGridProps> = ({
  items,
  onDownload,
  onSwitchToStyleClip,
  onRefetchVideos,
  onStartVideoPolling,
  onDelete,
  isLoading = false,
  currentPage,
  totalPages,
  onNextPage,
  onPrevPage,
}) => {
  const itemsPerPage = 16

  // 비디오 다운로드 모달 상태 관리
  const [selectedItem, setSelectedItem] = useState<StyleShotItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentModalIndex, setCurrentModalIndex] = useState(0)

  // 비디오 생성 확인 모달 상태 관리
  const [isVideoCreateModalOpen, setIsVideoCreateModalOpen] = useState(false)
  const [videoCreateItem, setVideoCreateItem] = useState<StyleShotItem | null>(
    null,
  )
  const [isVideoGenerating, setIsVideoGenerating] = useState(false)

  // 전체 이미지 모달 상태 관리
  const [isFullImageModalOpen, setIsFullImageModalOpen] = useState(false)
  const [fullImageJobId, setFullImageJobId] = useState<number | null>(null)

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  const handleNavigateModal = (index: number) => {
    if (index >= 0 && index < items.length) {
      setCurrentModalIndex(index)
      setSelectedItem(items[index])
    }
  }

  // 이미지 클릭 핸들러 (VideoDownloadModal과 통합)
  const handleItemClick = (item: StyleShotItem) => {
    const itemIndex = items.findIndex((i) => i.id === item.id)
    setSelectedItem(item)
    setCurrentModalIndex(itemIndex)
    setIsModalOpen(true)
  }

  // 비디오 아이콘 클릭 핸들러 (바로 VideoCreationConfirmModal 열기)
  const handleDirectVideoCreate = (item: StyleShotItem) => {
    setVideoCreateItem(item)
    setIsVideoCreateModalOpen(true)
  }

  // 비디오 생성 확인 모달 닫기
  const handleCloseVideoCreateModal = () => {
    setIsVideoCreateModalOpen(false)
    setVideoCreateItem(null)
  }

  // 전체 이미지 모달 핸들러
  const handleShowFullImage = (item: StyleShotItem) => {
    setFullImageJobId(item.jobId)
    setIsFullImageModalOpen(true)
  }

  const handleCloseFullImageModal = () => {
    setIsFullImageModalOpen(false)
    setFullImageJobId(null)
  }

  const handleVideoGenerate = async (item: StyleShotItem) => {
    try {
      const response = await aiStudioService.submitVideoSynthesisRequest({
        synthesizedImageUrls: [item.imageUrl],
        videoPrompt: 'Generate a short-form video from the style shot',
      })

      if (onSwitchToStyleClip) {
        onSwitchToStyleClip()
      }

      // 새로 생성된 비디오 폴링 시작
      if (onStartVideoPolling && response.data?.id) {
        setTimeout(() => {
          onStartVideoPolling(response.data!.id.toString())
        }, 100)
      }

      // 탭 이동 후 비디오 리스트 리패칭
      if (onRefetchVideos) {
        setTimeout(() => {
          onRefetchVideos()
        }, 100)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate video:', error)
      throw error
    }
  }

  const renderLoadingSkeletons = () => {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="h-[248px] w-[160px] animate-pulse rounded-[10px] bg-gray-300"
      />
    ))
  }

  const renderEmptyState = () => {
    return (
      <div className="col-span-full flex flex-col items-center justify-center py-20">
        <div className="text-center">
          <p className="mb-2 text-[18px] font-bold text-[#2E343D]">
            아직 생성된 Style Shot이 없어요!
          </p>
          <p className="text-[14px] text-[#8F929B]">
            AI 스타일룸에서 첫 번째 Style Shot을 만들어보세요.
          </p>
        </div>
      </div>
    )
  }

  // StyleShotItem을 GalleryItem으로 변환하는 헬퍼 함수
  const convertToGalleryItem = (item: StyleShotItem): GalleryItem => item

  return (
    <div className="w-full">
      <div
        className={`grid justify-items-center gap-1 ${isMobile ? 'grid-cols-2' : 'grid-cols-5'} `}
      >
        {isLoading
          ? renderLoadingSkeletons()
          : items.length > 0
            ? items.map((item) => (
                <GalleryItemComponent
                  key={item.id}
                  item={convertToGalleryItem(item)}
                  onDownload={() => onDownload(item)}
                  onVideoDownload={() => handleItemClick(item)}
                  onDirectVideoCreate={() => handleDirectVideoCreate(item)}
                  onShowFullImage={() => handleShowFullImage(item)}
                  onDelete={onDelete ? () => onDelete(item) : undefined}
                />
              ))
            : renderEmptyState()}
      </div>

      {/* 영상 다운로드 모달 */}
      <VideoDownloadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        item={selectedItem ? convertToGalleryItem(selectedItem) : null}
        allItems={items.map(convertToGalleryItem)}
        currentIndex={currentModalIndex}
        onNavigate={handleNavigateModal}
        onVideoGenerate={
          selectedItem ? () => handleVideoGenerate(selectedItem) : undefined
        }
        onDelete={
          onDelete && selectedItem ? () => onDelete(selectedItem) : undefined
        }
        currentPage={currentPage}
        totalPages={totalPages}
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
      />

      {/* 비디오 생성 확인 모달 */}
      <VideoCreationConfirmModal
        isOpen={isVideoCreateModalOpen}
        onClose={handleCloseVideoCreateModal}
        onConfirm={async () => {
          if (videoCreateItem) {
            setIsVideoGenerating(true)
            try {
              await handleVideoGenerate(videoCreateItem)
              handleCloseVideoCreateModal()
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Video generation failed:', error)
            } finally {
              setIsVideoGenerating(false)
            }
          }
        }}
        item={videoCreateItem ? convertToGalleryItem(videoCreateItem) : null}
        isLoading={isVideoGenerating}
      />

      {/* 전체 이미지 모달 */}
      <FullImageModal
        isOpen={isFullImageModalOpen}
        onClose={handleCloseFullImageModal}
        id={fullImageJobId}
      />
    </div>
  )
}
